import React, { useEffect, useState } from 'react';
import { Container as SContainer, LoadingTimeLabel } from '../components/styles';
import NootifyHeader from '../components/header';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import loginAnimation from "../animations/login";
import Lottie from "lottie-react";
import Overlay from '../components/Overlay';
import Box from '@mui/material/Box';
import * as SC from "../components/auth.styles";
import { useMutation, useQuery } from 'react-query';
import { navigate } from 'gatsby';
import { createAccountRequest, loginRequest, sendResetPasswordLinkRequest } from '../services/auth.service';
import Grid from '@mui/material/Grid';
import { getAuthToken, setAuthToken } from '../utils/utils';
import { red } from '@mui/material/colors';
import { useToasterControls } from '../context/ToasterContext';
import Seo from '../components/seo';
import {Link} from 'gatsby';

export const PANEL_TYPE = {
  LOGIN : "login",
  SIGNUP : "signup",
  RESET : "reset",
  RESET_PASSWORD : "reset_password",
  ACCOUNT_CREATED : "account_created"
};

const DEFAULT_ERROR = {
  emailLogin : "",
  emailSignup : "",
  passwordLogin : "",
  passwordSignup : "",
  rePasswordSignup : "",
  termsAgreed : "",
  requestError : ""
};

export default function() {
  const {addToaster} = useToasterControls();

  const [accountSuccesfullInfo, setAccountSucessfullInfo] = useState('');
  const [panelType, setPanelType] = useState(PANEL_TYPE.LOGIN);
  const [inputs, setInputs] = useState({
    emailLogin : "",
    emailSignup : "",
    passwordLogin : "",
    passwordSignup : "",
    rePasswordSignup : "",
    requestError : "",
    termsAgreed : false
  });
  const [errors, setErrors] = useState(DEFAULT_ERROR);

  const {isLoading, isError, refetch} = useQuery('auth', () => loginRequest(inputs.emailLogin, inputs.passwordLogin),{
    refetchOnWindowFocus: false,
    enabled: false,
    onSuccess : async (data) => {
      setAuthToken(data.token);
      setErrors(DEFAULT_ERROR);
      await navigate('/app')
    },
    onError : (error) => {
      if(error.hasOwnProperty('response')){
        switch(error.response.status){
          case 400:
          case 409:
            setErrors({...errors, passwordLogin : 'Incorrect e-mail or password.'});
            break;
          case 500:
          case 501:
          case 502:
          case 503:
          case 504:
            setErrors({...errors, passwordLogin : 'An unexpected error ocurred.'});
            break;
          default:
            break;
        }
      }
      else{
        setErrors({...errors, passwordLogin : 'An unexpected error ocurred.'});
      }
    },
    retry : 1
  });

  const resetPasswordMutation = useMutation(
    () => sendResetPasswordLinkRequest(inputs.emailLogin),
    {
      onSuccess: async () => {
        setAccountSucessfullInfo('An e-mail has been sent with the password reset link. Please also check your spam folder.');
        setPanelType(PANEL_TYPE.ACCOUNT_CREATED);
      },
      onError: () => {
        setErrors({...errors, requestError : 'Something went wrong while sending password reset link. Please try again later.'})
      },
    }
  );

  const loginMutation = useMutation(() => createAccountRequest(inputs.emailSignup, inputs.passwordSignup),{
    onSuccess: async () => {
     setAccountSucessfullInfo('Account has been created. A verification e-mail has been sent. Please verify your e-mail and then login. Thank you!');
     setPanelType(PANEL_TYPE.ACCOUNT_CREATED);
    },
    onError: (error) => {
      const response = error.response;
      const message = response.hasOwnProperty('data') ? response.data.message : 'Something went wrong while creating your account. Please try again later.';
      setErrors({...DEFAULT_ERROR, requestError : message})
    }
  });

  const onInputChange = event => setInputs({...inputs, [event.target.name] : event.target.value});

  const onLoginClick = async () => {
    const hasErrors = performValidationLogin();
    if(!hasErrors){
      await refetch();
    }
  };

  const onEnterKeyPress = (event, callback) => {
    if (event.key === 'Enter') {
      callback();
    }
  };

  const onResetPasswordButtonClick = async () => {
    const hasErrors = performValidationResetPassword();
    try{
      if(!hasErrors){
        await resetPasswordMutation.mutateAsync();
        setErrors({...errors, emailLogin : ""});
      }
    }catch(error){
      console.log(error?.response?.message);
    }
  };

  const performValidationResetPassword = () => {
    const { emailLogin } = inputs;

    let initialErrors = {...DEFAULT_ERROR};
    const updateErrors = (errorKey, newError) => initialErrors[errorKey] = newError;
    let hasErrors = false;

    if(!emailLogin){
      updateErrors('emailLogin', 'E-mail cannot be empty');
      hasErrors = true;
    }
    const re = /\S+@\S+\.\S+/;
    if(!re.test(emailLogin)){
      updateErrors('emailLogin', 'Incorrect e-mail format');
      hasErrors = true;
    }
    setErrors(initialErrors);
    return hasErrors;
  };

  const performValidationLogin = () => {
    const {emailLogin, passwordLogin} = inputs;
    let initialErrors = {...DEFAULT_ERROR};
    const updateErrors = (errorKey, newError) => initialErrors[errorKey] = newError;
    let hasErrors = false;

    if(!emailLogin){
      updateErrors('emailLogin', 'E-mail cannot be empty');
      hasErrors = true;
    }
    if(!passwordLogin){
      updateErrors('passwordLogin', 'Password cannot be empty');
      hasErrors = true;
    }
    setErrors(initialErrors);
    return hasErrors;
  }

  const performValidationsSignup = () => {
    const {emailSignup, passwordSignup, rePasswordSignup, termsAgreed} = inputs;
    let hasErrors = false;

    let initialErrors = {
      emailLogin : "",
      emailSignup : "",
      passwordLogin : "",
      passwordSignup : "",
      rePasswordSignup : "",
      termsAgreed : ""
    };
    const updateErrors = (errorKey, newError) => initialErrors[errorKey] = newError;

    /* Reset errored starte */
    setErrors(initialErrors);

    /*Perform email check*/
    if(!emailSignup){
      updateErrors('emailSignup', 'E-mail cannot be empty');
      hasErrors = true;
    }
    const re = /\S+@\S+\.\S+/;
    if(!re.test(emailSignup)){
      updateErrors('emailSignup', 'Incorrect e-mail format');
      hasErrors = true;
    }

    /*Perform password check*/
    if(!passwordSignup){
      updateErrors('passwordSignup', 'Password cannot be empty');
      hasErrors = true;
    }
    const passRegex = new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})");
    if(!passRegex.test(passwordSignup)){
      updateErrors('passwordSignup', 'Minimum eight characters, at least one uppercase letter, one lowercase letter,one number, one symbol.');
      hasErrors = true;
    }

    /*Perform re-typed password check*/
    if(!rePasswordSignup){
      updateErrors('rePasswordSignup', 'Password cannot be empty.');
      hasErrors = true;
    }
    if(passwordSignup !== rePasswordSignup){
      updateErrors('rePasswordSignup', 'Password do not match');
      hasErrors = true;
    }

    if(!termsAgreed){
      updateErrors('termsAgreed', 'You need to agree to Terms and Condition before continuing.');
      hasErrors = true;
    }

    setErrors(initialErrors);

    return hasErrors;
  };

  const renderPanelType = () => {
    switch(panelType){
      case PANEL_TYPE.LOGIN:
        return renderLogin();
      case PANEL_TYPE.SIGNUP:
        return renderSignUp();
      case PANEL_TYPE.RESET:
        return renderForgotPassword();
      case PANEL_TYPE.ACCOUNT_CREATED:
        return renderAccountCreated();
      default:
        return renderLogin();
    }
  };

  const renderSignUp = () => {
    return(<>
        <Grid container spacing={2} direction="column">
          <Grid item xs={2}>
            <TextField
              id="standard-helperText"
              label="E-mail"
              name={"emailSignup"}
              value={inputs.emailSignup}
              onChange={onInputChange}
              error={errors.emailSignup}
              fullWidth={true}
              type={"email"}
              helperText={errors.emailSignup}
              onKeyDown={(e) => onEnterKeyPress(e, onCreateAccountClick)}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="Password"
              type={"password"}
              name={"passwordSignup"}
              error={errors.passwordSignup}
              value={inputs.passwordSignup}
              onChange={onInputChange}
              onKeyDown={(e) => onEnterKeyPress(e, onCreateAccountClick)}
              size="small"
              fullWidth={true}
              helperText={errors.passwordSignup}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="Retype password"
              type={"password"}
              error={errors.rePasswordSignup}
              name={"rePasswordSignup"}
              value={inputs.rePasswordSignup}
              onChange={onInputChange}
              onKeyDown={(e) => onEnterKeyPress(e, onCreateAccountClick)}
              size="small"
              fullWidth={true}
              helperText={errors.rePasswordSignup}
              variant="outlined"
            />
          </Grid>
        </Grid>
      <FormControlLabel
        label={<Typography sx={{mt : 1}} variant="caption" color="textSecondary">
          I agree to the <Link to={"/terms"}>Terms and Conditions </Link>
           and <Link to={"/terms/privacy"}>Privacy Policy</Link>
        </Typography>}
        control={<Checkbox checked={inputs.termsAgreed} name="termsAgreed" onChange={onInputChange} defaultChecked size="small"/>}/>
      {errors.termsAgreed && <Typography variant="caption" color={red[700]}>{errors.termsAgreed}</Typography>}

      <SC.LoginButton onClick={onCreateAccountClick}>
        CREATE ACCOUNT
      </SC.LoginButton>
      <SC.AdditionalOptions>
        <span></span>
        <span onClick={() => setPanelType(PANEL_TYPE.RESET)}>Forgot password?</span>
      </SC.AdditionalOptions>
    </>)
  };

  const renderLogin = () => {
    return(
      <>
        <SC.FormBox>
          <Grid container spacing={2} direction="column">
            <Grid item xs={2}>
              <TextField
                id="standard-helperText"
                size="small"
                label="E-mail"
                name={"emailLogin"}
                fullWidth={true}
                type={"email"}
                error={errors.emailLogin}
                helperText={errors.emailLogin}
                value={inputs.emailLogin}
                onChange={onInputChange}
                onKeyDown={(e) => onEnterKeyPress(e, onLoginClick)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="Password"
                size="small"
                name={"passwordLogin"}
                type={"password"}
                error={errors.passwordLogin}
                helperText={errors.passwordLogin}
                value={inputs.passwordLogin}
                onChange={onInputChange}
                onKeyDown={(e) => onEnterKeyPress(e, onLoginClick)}
                fullWidth={true}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </SC.FormBox>
        <small>
          Note : Initial loading times might be higher for the alpha test version. Thank you for your patience!
        </small>

        <SC.LoginButton onClick={onLoginClick}>
          LOGIN
        </SC.LoginButton>
        <SC.AdditionalOptions>
          <span></span>
          <span onClick={() => setPanelType(PANEL_TYPE.RESET)}>Forgot password?</span>
        </SC.AdditionalOptions>
      </>
    )
  };

  const renderForgotPassword = () => {
    return(
      <>
        <SC.FormBox>
          <TextField
            id="standard-helperText"
            size="small"
            label="E-mail"
            name={"emailLogin"}
            error={errors.emailLogin}
            fullWidth={true}
            type={"email"}
            helperText={errors.emailLogin}
            value={inputs.emailLogin}
            onChange={onInputChange}
            onKeyDown={(e) => onEnterKeyPress(e, onResetPasswordButtonClick)}
            variant="outlined"
          />
          <Box sx={{ m: 0.5 }}>
            <Typography variant={"caption"}>
              Enter your account's email. If it exists, we'll send you a reset link. Also check your spam folder.
            </Typography>
          </Box>
        </SC.FormBox>
        <SC.LoginButton onClick={onResetPasswordButtonClick}>
          RESET PASSWORD
        </SC.LoginButton>
        <SC.AdditionalOptions>
          <span onClick={() => setPanelType(PANEL_TYPE.LOGIN)}>Back to Login</span>
          <span></span>
        </SC.AdditionalOptions>
      </>
    )
  };

  const renderAccountCreated = () => {
    return(
      <>
        <SC.FormBox>
          <Box sx={{ m: 0.5 }}>
            <Typography variant={"caption"}>
              {accountSuccesfullInfo}
            </Typography>
          </Box>
        </SC.FormBox>
        <SC.AdditionalOptions>
          <span onClick={() => setPanelType(PANEL_TYPE.LOGIN)}>Back to Login</span>
          <span></span>
        </SC.AdditionalOptions>
    </>
    );
  }


  const onCreateAccountClick = async () => {
    const isInvalid = performValidationsSignup();

    if(!isInvalid){
      setErrors({...DEFAULT_ERROR});
      loginMutation.mutate();
    }
  };

  const [transitionStarted, setTransitionStarted] = useState(false);

  const startTransition = () => {
    if(!transitionStarted){
      setTransitionStarted(true);
      setTimeout(() => setTransitionStarted(false), 200);
    }
  };

  const onPanelChange = (panel) => {
    setPanelType(panel);
    startTransition();
    setInputs({
      ...inputs,
      emailLogin : "",
      emailSignup : "",
      passwordLogin : "",
      passwordSignup : "",
      rePasswordSignup : "",
    })
  };

  /* Switch to app if authenticated */
  useEffect(() => {
    const switchToApp = async () => {
      const token = getAuthToken();
      if(token){
        await navigate('/app');
      }
    };
    switchToApp().catch(error => console.log(error));
  },[]);

  return(
    <SContainer>
      <Seo title={'Auth'}/>
      {isLoading || loginMutation.isLoading || resetPasswordMutation.isLoading ? <Overlay>
        <Lottie loop={true} animationData={loginAnimation} speed={2}/>
        <LoadingTimeLabel>Initial loading times slightly higher while in ALPHA. Thank you for your patience!</LoadingTimeLabel>
      </Overlay> : null}
      <NootifyHeader/>
      <SC.Wrapper isLogin={panelType === PANEL_TYPE.LOGIN} pendingRequest={isLoading}>
        <h2>Authentication</h2>
        {panelType !== PANEL_TYPE.RESET && panelType !== PANEL_TYPE.ACCOUNT_CREATED && <SC.ButtonsWrapper>
          <SC.PanelToggle onClick={() => onPanelChange(PANEL_TYPE.LOGIN)} >
            LOGIN
          </SC.PanelToggle>
          <SC.PanelToggle onClick={() => onPanelChange(PANEL_TYPE.SIGNUP)} >
            SIGN-UP
          </SC.PanelToggle>
          <SC.ActivePanel style={{height : transitionStarted ? '14px' : '38px'}} panel={panelType}>
            {panelType.toUpperCase()}
          </SC.ActivePanel>
        </SC.ButtonsWrapper>}
        {renderPanelType()}
        <Typography sx={{color : red[600]}}variant={"caption"}>
          {errors.requestError}
        </Typography>
      </SC.Wrapper>
    </SContainer>
  )
}